import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssistantValue } from "../../../../../../services/api/instances/assistantsApi/types";
import { TStoreData } from "../../../../../../services/api/instances/vectorStoreApi/types";
import { AppLabel } from "../../../../../../shared/components/AppLabel";
import { AssistantDatabaseButton } from "./AssistantDatabaseButton";
import { DatabaseActions } from "../../../../types";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import { selectAssistantValue } from "../../../../redux/selectors";
import { StoreSelect } from "../../../../../../common/Components/Selects/Components/StoreSelect";
import { TValueForService } from "../../../../../../services/api/instances/storageApi/types/service_types";
import { useInitDatabase } from "../../../../../Database/hooks/use-init-database";
import {
  selectFileLoading,
  selectLoading,
  selectStores,
} from "../../../../../Database/redux/selectors";
import { useAssistantField } from "../../../../hooks/use-assistant-field";
import { CreateStoreModal } from "../../../AssistantModals/CreateStoreModal";
import { AddFileToDatabaseModal } from "../../../AssistantModals/AddFileToDatabaseModal";
import { getValueWithStoreData } from "../../../../helpers/getValueWithStoreData";
import { TEntityInfo } from "../../../../../../common/types/utility";
import { useAppDispatch } from "../../../../../../services/redux/hooks/use-dispatch";
import { setStoreType } from "../../../../../Database/redux/slice";
import { TModalsIds } from "../../../../../../app/state/modalSlice/types";
import { useAppModals } from "../../../../../../app/state/modalSlice/hook/use-app-modals";
import { useStorageSelectors } from "../../../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import styles from "./styles.module.css";

interface Props {
  saveAssistant: (value: TValueForService<"assistants">) => void;
  openDatabaseModal: (id: TModalsIds, info: TEntityInfo | null) => void;
}

export const AssistantDatabaseBlock = (props: Props) => {
  const dispatch = useAppDispatch();
  const assistantValue = useAppSelector(selectAssistantValue);
  const toolResources = useAssistantField("tool_resources");
  const stores = useAppSelector(selectStores);
  const isStoreLoading = useAppSelector(selectLoading);
  const isFileLoading = useAppSelector(selectFileLoading);
  const { selectedObject } = useStorageSelectors("assistants");
  const { handleOpenModal, handleCloseModal } = useAppModals();
  const [mode, setMode] = useState<DatabaseActions>("");
  const { t } = useTranslation();

  useInitDatabase();

  const activeStore = useMemo(() => {
    const storeId = toolResources?.file_search?.vector_store_ids[0] ?? null;
    const activeStore = stores.find((item) => item.id === storeId) ?? null;
    setMode(activeStore === null ? "create" : "add_file");
    return activeStore;
  }, [stores, assistantValue?.assistant_object?.tools]);

  useEffect(() => {
    if (activeStore) {
      dispatch(setStoreType(activeStore.metadata.type));
    }
  }, [activeStore, selectedObject?.id]);

  const handleStoreChange = (store: TStoreData | null) => {
    setMode(store ? "add_file" : "create");
    if (!assistantValue?.assistant_object) return;
    const newValue: AssistantValue = getValueWithStoreData(
      store,
      assistantValue,
    );
    if (store) {
      dispatch(setStoreType(store.metadata.type));
    }
    props.saveAssistant(newValue);
  };

  const handleStoreCreated = (store: TStoreData) => {
    if (!assistantValue?.assistant_object) return;
    const newValue: AssistantValue = getValueWithStoreData(
      store,
      assistantValue,
    );
    props.saveAssistant(newValue);
  };

  return (
    <div>
      <AppLabel className={styles.text}>
        {t("assistants.knowledge_base_label")}
      </AppLabel>
      <div className={styles.container}>
        <StoreSelect
          emptyOptionLabel={t("assistants.no_knowledge_base")}
          store={activeStore}
          onSelectStore={handleStoreChange}
          stores={stores}
          isDisabled={isStoreLoading || isFileLoading}
        />
        <AssistantDatabaseButton
          isStoreLoading={isStoreLoading || isFileLoading}
          actionsHandler={() =>
            props.openDatabaseModal(
              mode === "create" ? "create_store_modal" : "add_file_to_database",
              null,
            )
          }
          mode={mode}
        />
      </div>
      <CreateStoreModal
        closeModal={handleCloseModal}
        callFileModal={handleOpenModal}
        onStoreCreated={handleStoreCreated}
      />
      <AddFileToDatabaseModal
        selectedStore={activeStore}
        closeModal={handleCloseModal}
      />
    </div>
  );
};
